import {
  keys, map, transform, find, get, has, head, flatMapDeep,
  sortBy, findKey, values, filter, isEmpty, isString, isNil, compact, omit,
  forEach,
} from 'lodash';
import {FormFragmentSchema, stringifyState} from 'apstra-ui-common';
import type {JSONSchema7, JSONSchema7TypeName} from 'json-schema';

import {queryParamToSorting} from '../queryParamUtils';
import humanizeString from '../humanizeString';
import MatcherFilterStringToObjectConverter from '../matcherFilterString/MatcherFilterStringToObjectConverter';
import checkForPatterns from './checkForPatterns';
import {
  DEFAULT_STAGE_DATA_SOURCE,
  DEFAULT_AGGREGATION_TYPE_BY_VALUE_TYPE,
  STAGE_DATA_SOURCE,
  VALUE_NUMBER_TYPES,
  AGGREGATION_TYPE_BY_VALUE_TYPE,
} from './consts';
import {
  DiscreteStateTimeline, DiscreteStateValue, EventTimeline, HorizontalBar,
  LineCharts, PrimitiveValue, TextValue
} from './components/valueRenderers';
import {
  Probe, ProbeProcessor, ProbeStage, RenderingStrategy,
  ProcessorDefinition, StageDefinition, StageDataSource, StageKeyType, StageValueType, StageValueSchema,
} from './types';

export function sortStagePropertyNames(propertyNames: string[]) {
  return sortBy(propertyNames, (propertyName) => propertyName === 'system_id' ? '' : propertyName);
}

export function getStageSorting({sortingOverrides, propertyNames}: {
  sortingOverrides: Record<string, 'asc' | 'desc'>,
  propertyNames: string[]
}) {
  return transform(sortStagePropertyNames(propertyNames), (result, propertyName) => {
    const name = `properties.${propertyName}`;
    if (!has(sortingOverrides, [name])) result[name] = 'asc';
  }, {...sortingOverrides});
}

export function processorCanRaiseAnomalies(processor: ProbeProcessor): boolean {
  return processor.type === 'anomaly' || processor.properties.raise_anomaly;
}

export function processorHasRaiseAnomaliesFlag(processor: ProbeProcessor): boolean {
  return has(processor.properties, ['raise_anomaly']);
}

export function processorCanRaiseWarnings(processor: ProbeProcessor): boolean {
  return (
    processor.type === 'interface_queue_stats' ||
    has(processor, ['properties', 'service_interval']) ||
    get(processor, ['properties', 'enable_telemetry_service']) === true
  );
}

export function convertKeyType(type: StageKeyType): JSONSchema7TypeName | undefined {
  if (type === 'bool') {
    return 'boolean';
  } else if (type === 'float' || type === 'integer') {
    return 'number';
  } else if (type === 'string') {
    return 'string';
  }
}

export function convertValueType(type: StageValueType): JSONSchema7TypeName {
  if (type === 'bool') {
    return 'boolean';
  } else if (VALUE_NUMBER_TYPES.has(type)) {
    return 'number';
  } else {
    return 'string';
  }
}

function getBaseRenderValueAs({type, possible_values: possibleValues}: StageValueSchema, dataSource: StageDataSource) {
  const convertedType = convertValueType(type);
  if (convertedType === 'string') {
    if (possibleValues?.length) {
      if (dataSource === STAGE_DATA_SOURCE.real_time) return DiscreteStateValue;
      if (dataSource === STAGE_DATA_SOURCE.time_series) return DiscreteStateTimeline;
    } else {
      if (dataSource === STAGE_DATA_SOURCE.real_time) return TextValue;
      if (dataSource === STAGE_DATA_SOURCE.time_series) return EventTimeline;
    }
  } else if (convertedType === 'number') {
    if (dataSource === STAGE_DATA_SOURCE.real_time) return HorizontalBar;
    if (dataSource === STAGE_DATA_SOURCE.time_series) return LineCharts;
  }
  return PrimitiveValue;
}

export function getBaseRenderingStrategy(stage: ProbeStage, dataSource: StageDataSource): RenderingStrategy {
  const values = keys(stage.values);
  if (values.length === 0) return {};
  return {
    sortableByValue: dataSource === STAGE_DATA_SOURCE.real_time,
    showTimestamp: dataSource === STAGE_DATA_SOURCE.real_time,
    noSpotlightMode: values.length > 1 && dataSource === STAGE_DATA_SOURCE.real_time,
    graphCombiningAvailable: dataSource === STAGE_DATA_SOURCE.real_time ?
      false :
      ({type}: any = {}) => VALUE_NUMBER_TYPES.has(type),
    getRenderer: (valueSchema) => getBaseRenderValueAs(valueSchema, dataSource),
  };
}

export function getStageRenderingStrategy(
  args: {stage: ProbeStage, dataSource: StageDataSource} & (
    {showContext?: false;} |
    {showContext: true; probe: Probe;}
  )
): RenderingStrategy {
  const {stage, dataSource = DEFAULT_STAGE_DATA_SOURCE, showContext} = args;
  const stageRenderingStrategy = getBaseRenderingStrategy(stage, dataSource);
  if (!showContext) return stageRenderingStrategy;

  const patternDescription = checkForPatterns({probe: args.probe, stageName: stage.name, dataSource});
  if (patternDescription) {
    const patternRenderingStrategy = patternDescription.renderingStrategy;
    const combinedRenderValueAs = (valueSchema) => {
      for (const {ValueRenderer, getRenderer} of [patternRenderingStrategy, stageRenderingStrategy]) {
        const renderer = getRenderer?.(valueSchema) ?? ValueRenderer;
        if (renderer) return renderer;
      }
      return null;
    };
    return omit(
      {
        ...stageRenderingStrategy,
        ...patternRenderingStrategy,
        getRenderer: combinedRenderValueAs
      },
      ['ValueRenderer']);
  } else {
    return stageRenderingStrategy;
  }
}

export function getTelemetryServiceSchema(items: any[]) {
  if (isEmpty(items)) return [];
  const properties = keys(head(items)?.properties);
  const schema: any[] = map(properties, (propertyName) => ({
    propertyName,
    label: humanizeString(propertyName),
    name: `properties.${propertyName}`,
    value: ['properties', propertyName],
    sortable: true,
  }));
  schema.push({
    name: 'warning',
    label: 'Telemetry Service Status',
    value: ['warning'],
  });
  return schema;
}

export function getStageKeyLabels(
  {probe, processor, processorDefinitions, keyNames}:
  {probe: Probe, processor: ProbeProcessor, processorDefinitions: ProcessorDefinition[], keyNames: string[]}
) {
  const keysToLabel = new Set(keyNames);
  const labeledKeys = {};
  const visitedProcessors = new Set<ProbeProcessor>();
  const processorDefinitionsByType = transform(processorDefinitions, (result, processorDefinition) => {
    result[processorDefinition.name] = processorDefinition;
  }, {});
  const extractLabelsFromProcessor = (processor: ProbeProcessor) => {
    if (visitedProcessors.has(processor)) return;
    visitedProcessors.add(processor);
    keysToLabel.forEach((keyName) => {
      const label = get(processorDefinitionsByType, [processor.type, 'schema', 'properties', keyName, 'title']);
      if (label) {
        labeledKeys[keyName] = label;
        keysToLabel.delete(keyName);
      }
    });
    if (!keysToLabel.size) return;
    forEach(processor.inputs, (input) => {
      if (input?.stage) {
        const sourceProcessor = getProcessorByStageName({probe, stageName: input.stage});
        if (sourceProcessor) {
          extractLabelsFromProcessor(sourceProcessor);
          if (!keysToLabel.size) return false;
        }
      }
    });
  };
  extractLabelsFromProcessor(processor);
  return labeledKeys;
}

export function getStageDataSchema(
  {probe, processor, stage, renderingStrategy, processorDefinitions, showValue = true, valueColumnName}:
  {
    probe: Probe, processor: ProbeProcessor, stage: ProbeStage, renderingStrategy: RenderingStrategy,
    processorDefinitions: ProcessorDefinition[], showValue?: boolean, valueColumnName?: string
  }
) {
  const {
    sortableByValue = false,
    showTimestamp = false,
    extraColumns = []
  } = renderingStrategy;
  const keyLabels = getStageKeyLabels({probe, processor, processorDefinitions, keyNames: keys(stage.keys)});
  const properties = map(sortStagePropertyNames(keys(stage.keys)), (propertyName) => ({
    name: `properties.${propertyName}`,
    label: keyLabels[propertyName] ?? humanizeString(propertyName),
    propertyName,
    propertyType: stage.keys[propertyName],
  }));
  const stageDataSchema: any[] = properties.map(({name, label, propertyName, propertyType}) => ({
    name,
    label,
    value: ['properties', propertyName],
    key: true,
    sortable: true,
    type: propertyType,
  }));
  if (!isEmpty(extraColumns)) {
    stageDataSchema.push(...extraColumns);
  }
  if (processorCanRaiseAnomalies(processor)) {
    stageDataSchema.push({
      name: 'anomaly',
      label: 'Anomaly',
    });
  }
  if (showValue) {
    values(stage.values)
      .filter((value) => !valueColumnName || valueColumnName === value.name)
      .forEach(({name, title, type}) => {
        stageDataSchema.push({
          name,
          label: title || humanizeString(name),
          value: [name],
          sortable: sortableByValue,
          type,
        });
      });
  }
  if (showTimestamp) {
    stageDataSchema.push({
      name: 'timestamp',
      label: 'Updated',
      sortable: true,
      value: ['timestamp'],
    });
  }
  return stageDataSchema;
}

export function getStageFormSchema(stage: ProbeStage, stageDefinition: StageDefinition): FormFragmentSchema {
  return [
    {
      name: 'enable_metric_logging',
      schema: {
        type: 'boolean',
        title: 'Enable Metric Logging',
        description: 'Save changes in this stage to MetricDB time series database.'
      }
    },
    {
      name: 'retention_duration',
      hidden: !stage?.enable_metric_logging,
      schema: {
        type: 'number',
        default: 86400,
        title: 'Retention Duration',
        description: 'Retain data in MetricDB for specified duration.'
      }
    },
    {
      name: 'description',
      schema: {
        type: 'string',
        title: 'Description',
      }
    },
    {
      name: 'graph_annotation_properties',
      schema: {
        type: 'object',
        title: 'Graph Annotation Properties',
        description: 'Annotate graph nodes with properties associated with stage values.',
      }
    },
    {
      name: 'units',
      schema: {
        type: 'object',
        title: 'Units',
        description: 'Units for values.',
        properties: transform(getPossibleStageValues(stageDefinition), (result, {name}) => {
          result[name] = {type: 'string'};
        }, {}),
      }
    }
  ];
}

export function normalizeStageUnits(units: Record<string, string> | undefined, unitsSchema: JSONSchema7) {
  return transform(unitsSchema.properties!, (result, propertySchema, propertyName) => {
    const propertyUnits = units?.[propertyName];
    if (propertyUnits) result[propertyName] = propertyUnits;
  }, {});
}

export function castFilterConverter(filter: string) {
  if (isString(filter)) {
    const filterObject = MatcherFilterStringToObjectConverter.parseAndConvert(filter);
    return isEmpty(filterObject) && !isEmpty(filter) ? filter : filterObject;
  }
  return filter;
}

export function getStageQueryParamsFromWidget({
  widget, stage, alwaysUseContext, filterDeserializer = castFilterConverter
}) {
  const stageDataSourceIsTimeSeries = widget.data_source === STAGE_DATA_SOURCE.time_series;
  const filter = filterDeserializer(widget.filter);
  const filters: any = {
    dataSource: widget.data_source,
  };
  if (!isEmpty(filter)) {
    filters.filter = filter;
  }
  if (!stageDataSourceIsTimeSeries) {
    filters.anomalousOnly = widget.anomalous_only;
  }
  if (!stageDataSourceIsTimeSeries || alwaysUseContext) {
    filters.showContextInfo = widget.show_context;
  }
  if (stageDataSourceIsTimeSeries) {
    filters.valueColumnName = getValueColumnNameFromWidget({widget, stage});
  }
  if (!isNil(widget.time_series_duration)) {
    filters.timeSeriesDuration = widget.time_series_duration;
  }
  if (!isNil(widget.aggregation_period)) {
    filters.timeSeriesAggregation = widget.aggregation_period;
  }
  if (widget.combine_graphs) {
    filters.combineGraphs = widget.combine_graphs;
  }
  if (widget.aggregation_type) {
    filters.aggregationType = widget.aggregation_type;
  }

  const stageFilter: any = {};
  const sorting = queryParamToSorting(widget.orderby);
  if (!isEmpty(sorting)) {
    stageFilter.sorting = sorting;
  }
  if (!isEmpty(filters)) {
    stageFilter.filters = filters;
  }

  return isEmpty(stageFilter) ? null : `?stage-filter=${stringifyState(stageFilter)}`;
}

export function getValueColumnNameFromWidget({widget, stage}) {
  if (widget.value_column_name) return widget.value_column_name;
  // FIXME(vkramskikh): there is currently no value_column_name in the widget.
  // Instead, value_column_name is determined from visible_columns. This should be fixed
  // by introducing value_column_name support on the backend.
  if (!stage || widget.data_source === STAGE_DATA_SOURCE.real_time) return null;
  return find(widget.visible_columns, (name) => name in stage.values) ?? head(keys(stage.values)) ?? null;
}

export function getStageComponentPropsFromWidget({widget, probes}) {
  const {probe_id: probeId, stage_name: stageName} = widget;
  const probe = find(probes, {id: probeId});
  const processor = getProcessorByStageName({probe, stageName});
  const stage = getStageByName({probe, stageName});
  return {probe, processor, stage};
}

export function generateStageQueryParams(params) {
  if (params) {
    const filter = stringifyState({
      ...params.filters && {filters: {
        filter: params.filters.filter,
        anomalousOnly: params.filters.anomalousOnly,
        showContextInfo: params.filters.showContextInfo,
        dataSource: params.filters.dataSource,
        combineGraphs: params.filters.combineGraphs,
      }},
      ...params.sorting && {sorting: queryParamToSorting(params.sorting)},
    });
    return `?stage-filter=${filter}`;
  }
  return null;
}

export function getStageByName({probe, stageName}: {
  probe: Probe,
  stageName: string
}): ProbeStage | null {
  if (!probe) return null;
  return find(probe.stages, {name: stageName}) || null;
}

export function getProcessorByStageName({probe, stageName}: {
  probe: Probe,
  stageName: string
}): ProbeProcessor | null {
  if (!probe) return null;
  return find(
    probe.processors,
    ({outputs}) => !!find(outputs, (outputName) => outputName === stageName)
  ) || null;
}

export function getOutputNameByStageName({processor, stageName}: {
  processor: ProbeProcessor,
  stageName: string
}): string | null {
  if (!processor || !processor.outputs || !stageName) {
    return null;
  }
  return findKey(processor.outputs, (outputStageName) => outputStageName === stageName) || null;
}

export function getInputStages({probe, processor}: {probe: Probe, processor: ProbeProcessor}) {
  return compact(map(
    getInputStageNames(processor),
    (stageName) => stageName ? getStageByName({probe, stageName}) : null
  ));
}

export function getInputStageNames(processor: ProbeProcessor) {
  return filter(map(processor.inputs, ({stage}) => stage));
}

export function getPossibleStageValues(stageDefinition: StageDefinition): StageValueSchema[] {
  return flatMapDeep(stageDefinition?.types, ({dynamic, values}) => {
    return map(values, (valueArray) => map(valueArray, (value) => ({...value, dynamic})));
  });
}

export function getMatchingStageValueSchemas(
  inputValues: StageValueSchema[] | undefined = [],
  outputValues: StageValueSchema[] | undefined = [],
): StageValueSchema[] {
  return isEmpty(inputValues) ? outputValues : filter(outputValues, (output) => {
    const condition = (input, output) =>
      (input.name === '*' || input.name === output.name) &&
      input.type === output.type &&
      input.dynamic === output.dynamic &&
      !!input.possible_values === !!output.possible_values;
    return find(inputValues, (input) => condition(input, output));
  }) as StageValueSchema[];
}

export function generateNewInputName(processor: ProbeProcessor, from = 1) {
  const result = 'in' + from;
  if (!(result in processor.inputs)) return result;
  return generateNewInputName(processor, from + 1);
}

export function getPossibleValueColumnOptions(stage: ProbeStage) {
  return values(stage.values).map(({name, title}) => ({
    key: name,
    value: name,
    text: title || humanizeString(name),
  }));
}

export function getPossibleAggregationTypes(stage: ProbeStage, valueColumnName: string) {
  const type = get(stage, ['values', valueColumnName, 'type']);
  return transform(AGGREGATION_TYPE_BY_VALUE_TYPE, (result, types, aggregationType) => {
    if (types.has(type)) {
      result.push(aggregationType);
    }
  }, [] as string[]);
}

export function getDefaultAggregationType(stage: ProbeStage, valueColumnName: string, patternDescription) {
  if (patternDescription?.renderingStrategy.aggregationType) {
    return patternDescription.renderingStrategy.aggregationType;
  }
  const type = convertValueType(get(stage, ['values', valueColumnName, 'type']));
  return DEFAULT_AGGREGATION_TYPE_BY_VALUE_TYPE[type];
}
